"use client";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form, FormLabel, FormMessage } from "@/components/ui/form";
import Buttons from "@/components/core/Buttons";
import {
  CreateEmployeeData,
  CreateEmployeeSchema,
  UpdateEmployeeData,
  UpdateEmployeeSchema,
} from "@/components/shared/Forms/EditorEmployeeForm/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IGetEmployeeAction } from "@/actions/employee/types";
import * as React from "react";
import { useEffect, useState } from "react";
import MultipleSelector from "@/components/shared/Forms/Fields/MultipleSelector";

interface IEditorEmployeeFormProps {
  employee?: IGetEmployeeAction["Response"];
  onSuccess?: () => void;
}

const EditorEmployeeForm = ({
  employee,
  onSuccess,
}: IEditorEmployeeFormProps) => {
  const { toast } = useToast();

  const [valueMultipleSelector, setValueMultipleSelector] = useState<
    { label: string; value: string }[]
  >([]);
  const isEdit = !!employee?.id;

  const form = useForm<CreateEmployeeData | UpdateEmployeeData>({
    resolver: zodResolver(isEdit ? UpdateEmployeeSchema : CreateEmployeeSchema),
    defaultValues: {
      ...employee,
      active: employee?.active ?? true,
      vocation_ids: employee?.vocations?.map((vocation) => vocation.id),
    },
  });

  const {
    data: salonsData,
    isLoading: isLoadingSalons,
    isError: isErrorSalons,
  } = hooks.useGetSalons(1, 10);

  const selectedSalonId = form.watch("salon_id");
  const selectedUserId = form.watch("user_id");

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
  } = hooks.useGetUserBySalon(selectedSalonId);

  const {
    data: vocationsData,
    isLoading: isLoadingVocations,
    isError: isErrorVocations,
  } = hooks.useGetVocationBySalonId(selectedSalonId);

  const salons = salonsData?.data;
  const users = usersData;
  const availableVocations = vocationsData?.map((vocation) => ({
    label: vocation.name,
    value: vocation.id,
  }));

  useEffect(() => {
    if (!selectedUserId || !users) return;

    const selectedUser = users.find((user) => user.id === selectedUserId);

    if (!selectedUser) return;

    form.setValue("name", selectedUser.name);
    form.setValue("email", selectedUser.email);
    form.setValue("phone", selectedUser.phone);
  }, [selectedUserId, users, form]);

  useEffect(() => {
    if (employee) {
      form.reset({
        ...employee,
        vocation_ids: employee.vocations.map((v) => v.id),
      });

      setValueMultipleSelector(
        employee.vocations.map((v) => ({
          label: v.name,
          value: v.id,
        })),
      );
    }
  }, [employee, form]);

  const updateEmployeeService = hooks.useUpdateEmployee({
    onSuccess: () => {
      toast({
        title: "Colaborador atualizado com sucesso!",
        variant: "success",
      });
      if (onSuccess) onSuccess?.();
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível atualizar o colaborador",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: CreateEmployeeData | UpdateEmployeeData) {
    if (isEdit && employee) {
      const { id, created_at, updated_at, deleted_at, ...payload } =
        data as UpdateEmployeeData;

      await updateEmployeeService.mutateAsync({
        id: employee.id,
        created_at: created_at ?? "",
        updated_at: updated_at ?? undefined,
        deleted_at: deleted_at ?? undefined,
        ...payload,
      });
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full flex flex-col items-center gap-y-6"
      >
        <div className={"w-full flex flex-col items-start"}>
          <FormLabel className={"font-semibold text-sm"}>Salão</FormLabel>
          <Controller
            control={form.control}
            name="salon_id"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  form.setValue("vocation_ids", []);
                  setValueMultipleSelector([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um salão" />
                </SelectTrigger>

                <SelectContent>
                  {isLoadingSalons && (
                    <SelectItem value={field.value} disabled>
                      Carregando salões...
                    </SelectItem>
                  )}

                  {!isLoadingSalons &&
                    !isErrorSalons &&
                    salons?.map((salon) => (
                      <SelectItem key={salon.id} value={salon.id}>
                        {salon.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className={"w-full flex flex-col items-start"}>
          <FormLabel className={"font-semibold text-sm"}>Usuário</FormLabel>
          <Controller
            control={form.control}
            name="user_id"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um usuário" />
                </SelectTrigger>

                <SelectContent title="Salão">
                  {!selectedSalonId && (
                    <SelectItem value="disabled" disabled>
                      Selecione um salão primeiro
                    </SelectItem>
                  )}
                  {isLoadingUsers && (
                    <SelectItem value="loading" disabled>
                      Carregando usuários...
                    </SelectItem>
                  )}

                  {isErrorUsers && (
                    <SelectItem value="error" disabled>
                      Erro ao carregar usuários
                    </SelectItem>
                  )}

                  {!isLoadingUsers &&
                    !isErrorUsers &&
                    users?.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className={"w-full flex flex-col items-start"}>
          <FormLabel className={"font-semibold text-sm"}>Serviços</FormLabel>
          {isLoadingVocations ? (
            <>
              <MultipleSelector
                disabled={isLoadingVocations}
                placeholder={
                  isLoadingVocations ? "Carregando vocações..." : "Selecione..."
                }
                emptyIndicator={
                  isLoadingVocations
                    ? "Carregando..."
                    : "Nenhuma vocação encontrada."
                }
              />
            </>
          ) : (
            <Controller
              control={form.control}
              name="vocation_ids"
              render={({ field }) => (
                <div className="w-full">
                  <MultipleSelector
                    key={selectedSalonId}
                    value={valueMultipleSelector}
                    onChange={(newValue) => {
                      setValueMultipleSelector(newValue);
                      field.onChange(newValue.map((v) => v.value));
                    }}
                    defaultOptions={availableVocations}
                    disabled={isLoadingVocations}
                    placeholder={
                      isLoadingVocations
                        ? "Carregando vocações..."
                        : "Selecione..."
                    }
                    emptyIndicator={
                      isLoadingVocations
                        ? "Carregando..."
                        : "Nenhuma vocação encontrada."
                    }
                    badgeClassName="bg-[#4b5563] text-content-primary hover:bg-[#35393e] duration-300 ease-linear"
                  />
                  {form.formState.errors.vocation_ids && (
                    <FormMessage>
                      {form.formState.errors.vocation_ids.message}
                    </FormMessage>
                  )}
                </div>
              )}
            />
          )}
        </div>

        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title="Salvar Alterações"
          isLoading={updateEmployeeService.isPending}
        />
      </form>
    </Form>
  );
};

export default EditorEmployeeForm;
